import React, { useEffect, useState } from 'react';
import { Form, Input, Button, Typography, Icon, Alert, message } from 'antd';
import BGimage from "../../../assets/images/bg2.png";
import { withRouter} from 'react-router';
import { AuthenticationService } from '../../api/Auth';
import AppConfig from '../../config/AppConfig';
import I18n from '../../common/I18n';
import { CompanyLogo } from '../common/LogisticsLoadingScreen';
import { normalizeAcademyReturnPath } from '../../helpers/common';
import oktaLogo from '../../../assets/images/okta.svg';

const { Item } = Form;
const { Title } = Typography;

// Parse Okta callback error from URL (e.g. user blocked, access_denied)
const getOktaCallbackError = () => {
  if (typeof window === 'undefined' || !window.location.search) return null;
  const params = new URLSearchParams(window.location.search);
  const error = params.get('error');
  const description = params.get('error_description');
  if (!error) return null;
  return {
    error,
    description: description ? decodeURIComponent(description.replace(/\+/g, ' ')) : null,
  };
};


const AuthForm = ({ form, history , setIsAuthenticated , resetPasswordToken }) => {
  const [ operation, setOperation ] = useState('LOG_IN');
  const [ rememberMe, setRememberMe ] = useState(false);
  const [ isLoading, setIsLoading ] = useState(false);
  const [ ssoError, setSsoError ] = useState(() => getOktaCallbackError());

  const backgroundImageStyle = {
    backgroundImage: `url(${BGimage})`,
    backgroundSize: 'contain',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    height: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  };

  const formStyle = {
    background: 'rgba(255, 255, 255, 0.9)',
    padding: '20px',
    borderRadius: '10px',
    maxWidth: '400px',
    width: '100%',
    boxShadow: '0px 0px 20px rgba(0, 0, 0, 0.2)',
  };

  useEffect(() => {
    if (resetPasswordToken && resetPasswordToken.length) {
      setOperation('NEW_PASSWORD');
    }
  }, []);

  useEffect(() => {
    if (operation === "VALIDATE_OTP") {
      setOperation("FORGOT_PASSWORD");
    }
  }, [form.getFieldValue('email')]);

  const handleOktaLogin = () => {
    const samlBase = AppConfig.oktaSamlUri;
    if (samlBase) {
      let forceAuthn = false;
      try {
        forceAuthn = sessionStorage.getItem('fe_saml_force_authn_next') === '1';
        if (forceAuthn) sessionStorage.removeItem('fe_saml_force_authn_next');
      } catch (e) {
        /* ignore */
      }
      const qs = forceAuthn ? '?forceAuthn=1' : '';
      window.location.href = `${samlBase}/saml/login${qs}`;
      return;
    }
    message.warning(I18n.t('auth_messages.sso_not_configured'));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    form.validateFields((err, values) => {
      if (!err) {
        setIsLoading(true);
        AuthenticationService.login(values.email, values.password).then((response) => {
          if (response.success) {
            localStorage.setItem("auth", JSON.stringify(response.data));
            const returnToRaw = new URLSearchParams(window.location.search).get("return_to");
            const academyPath = normalizeAcademyReturnPath(returnToRaw);
            if (academyPath) {
              sessionStorage.setItem("academy_return_path", academyPath);
              const base = String(AppConfig.academyUrl || "").replace(/\/$/, "");
              const { access_token: accessToken, user_id: userId } = response.data;
              window.location.href = `${base}/auth/validate?token=${encodeURIComponent(accessToken)}&resource_owner_id=${encodeURIComponent(userId)}&redirect=${encodeURIComponent(academyPath)}`;
              return;
            }
            message.success(I18n.t("auth_messages.login_success"));
            history.push('/');
          } else {
            const errorMessage = response.auth_source === 'okta'
              ? I18n.t("auth_messages.use_sso")
              : (response.error || I18n.t("auth_messages.error_msg"));
            message.error(errorMessage);
          }
        }).finally(() => {
          setIsLoading(false);
        });
      }
    });
  };

  const handleOtpValidate = (e) => {
    e.preventDefault();
    form.validateFields((err, values) => {
      if (!err) {
        setIsLoading(true);
        AuthenticationService.validateOtp(values.email, values.phoneOtp)
          .then((response) => {
            if (response.success) {
              message.success(I18n.t("auth_messages.otp_verified"));
              window.location.assign(`${AppConfig.baseUrl}/forgot_password?reset_password_token=${response.resetToken}`);
            } else {
              message.error(response.error || I18n.t("auth_messages.error_msg"));
            }
          })
          .finally(() => {
            setIsLoading(false);
          });
      }
    });
  };


  const handleForgotPassword = () => {
    setOperation('FORGOT_PASSWORD');
  };

  const handleForgotPasswordSubmit = (e) => {
    e.preventDefault();
    form.validateFields((err, values) => {
      const numberPattern = AppConfig.regexMobile;///^\+?[1-9]\d{1,14}$/;
      if (!err) {
        setIsLoading(true);
        if (typeof values.email === 'string' && numberPattern.test(values.email)) {
          AuthenticationService.sendOtp(values.email)
            .then((response) => {
              if (response.success) {
                message.success(response.sms_msg || I18n.t("auth_messages.otp_sent"));
                setOperation("VALIDATE_OTP");
              } else {
                message.error(response.error || I18n.t("auth_messages.error_msg"));
              }
            })
            .finally(() => {
              setIsLoading(false);
            });
        } else {
        AuthenticationService.forgotPassword(values.email).then((response) => {
          if (response.success) {
            message.success(I18n.t("auth_messages.reset_email"));
            setOperation('LOG_IN');
          } else {
            message.error(response.error || I18n.t("auth_messages.error_msg"));
          }
        }
        ).finally(() => {
          setIsLoading(false);
        }
        );
        }
      }
    });
  };

  const { getFieldDecorator } = form;

  const handlePasswordChange = (e) => {
    e.preventDefault();
    // check if password and confirm password are same
    form.validateFields((err, values) => {
      if (!err) {
        if(values.password === values.confirmpassword){
          setIsLoading(true);
          AuthenticationService.changePassword({
            token : resetPasswordToken,
            password : values.password,
            password_confirmation : values.confirmpassword
          }).then((response) => {
            if (response.success) {
              message.success(I18n.t("auth_messages.password_changed"), 2, () => {
                window.location.href = '/login';
              });
              

            } else {
              message.error(response.error || I18n.t("auth_messages.error_msg"));
            }
          }).finally(() => {
            setIsLoading(false);
          });
        }else{
          message.error(I18n.t("auth_messages.reset_password_match"));
        }
      }
    });
  };

  // Clear error params from URL once we've read them so refresh doesn't re-show the error
  useEffect(() => {
    if (!ssoError) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('error');
    url.searchParams.delete('error_description');
    url.searchParams.delete('state');
    window.history.replaceState({}, document.title, url.pathname + url.search);
  }, [ssoError]);

  const ssoErrorMessage = ssoError
    ? (ssoError.description || (ssoError.error === 'unauthorized' ? I18n.t('auth_messages.sso_error_user_blocked') : I18n.t('auth_messages.sso_error_generic')))
    : null;

  return (
    <div style={ backgroundImageStyle } data-testid="login-page">
      <div style={ formStyle }>
        {/* use a image to put the logo svg */ }
        <div style={{ textAlign: 'center' }}>
          <CompanyLogo style={{ width: '65%', height: 'auto', marginBottom: 20 }} />
        </div>
        { ssoErrorMessage && (
          <Alert
            type="error"
            message={I18n.t('auth_messages.sso_error_title')}
            description={ssoErrorMessage}
            showIcon
            closable
            onClose={() => setSsoError(null)}
            style={{ marginBottom: 20 }}
          />
        ) }

        <Title level={3} style={{ display: 'block', textAlign: 'center', marginBottom: 8, fontWeight: 600 }}>
        { operation === 'LOG_IN' ? 'Welcome' : operation === 'FORGOT_PASSWORD' ? 'Forgot password' : operation === 'VALIDATE_OTP' ? 'Recovery password' : 'Create new password' }
        </Title>

        { operation === 'LOG_IN' && (
          <p style={{ textAlign: 'center', color: 'rgba(0,0,0,0.45)', fontSize: 14, marginBottom: 24 }}>
            Log in to Fleet Enable to continue.
          </p>
        ) }
        { operation !== 'LOG_IN' && <div style={{ marginBottom: 20 }} /> }

        <Form onSubmit={ operation === 'FORGOT_PASSWORD' ? handleForgotPasswordSubmit : operation === "NEW_PASSWORD" ? handlePasswordChange : operation === 'VALIDATE_OTP' ? handleOtpValidate : handleSubmit } className="login-form" data-testid="login-form">
          { operation !== 'NEW_PASSWORD' && (
            <Item label={I18n.t("auth_messages.username_lable")}>
              { getFieldDecorator('email', {
                rules: [
                  {
                    required: true,
                    message: I18n.t("auth_messages.email_phone"),
                  },
                  {
                    pattern: /^([a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4})|(\d{10})$/,
                    message: I18n.t("auth_messages.username_validation_msg"),
                  },
                ],
                validateTrigger: 'onBlur'
              })(<Input data-testid="login-username-input" autoFocus autoComplete="off" placeholder={I18n.t("auth_messages.username_placeholder")} style={{ marginBottom: 0 }} />) }
            </Item>
          ) }

          { operation === 'VALIDATE_OTP' && (
            <Item label="Enter OTP">
            {getFieldDecorator('phoneOtp', {
              rules: [
                {
                  required: true,
                  message: 'Please input your OTP!',
                }
              ],
              validateTrigger: 'onBlur',
            })(<Input data-testid="login-otp-input" autoFocus autoComplete="off" placeholder={I18n.t("auth_messages.otp_placeholder")} />)}
          </Item>
          )}
          
          { !['FORGOT_PASSWORD', 'VALIDATE_OTP'].includes(operation) && (
            <>
              <Item label={ operation === 'NEW_PASSWORD' ? 'New Password' : 'Password' }>
                { getFieldDecorator('password', {
                  rules: [
                    {
                      required: true,
                      message: I18n.t("auth_messages.password_validation_msg"),
                    },
                    {
                      min: 6,
                      message: 'Password must be at least 6 characters long!',
                    },
                  ],
                  validateTrigger: 'onBlur'
                })(<Input.Password data-testid="login-password-input" autoComplete="off" />) }
              </Item>
              { operation === 'NEW_PASSWORD' && (
                <Item label="Confirm Password">
                  { getFieldDecorator('confirmpassword', {
                    rules: [
                      {
                        required: true,
                        message: I18n.t("auth_messages.password_validation_msg"),
                      },
                      {
                        min: 6,
                        message: I18n.t("auth_messages.password_length"),
                      },
                    ],
                  })(<Input.Password data-testid="login-confirm-password-input" autoComplete="off" />) }
                </Item>
              )}
              {/* <Item name="remember_me" valuePropName="checked">
                <Checkbox onChange={ (e) => setRememberMe(e.target.checked) }>
                  Remember me
                </Checkbox>
              </Item> */}
            </>
          ) }
          <Item>
            <Button type="primary" htmlType="submit" block loading={ isLoading } data-testid="login-submit-button">
              { operation === 'LOG_IN' ? 'Submit' : operation === 'FORGOT_PASSWORD' ? 'Send reset instruction' : operation === 'VALIDATE_OTP' ? 'Validate OTP' : 'Save' }
            </Button>
          </Item>
          { ((operation !== 'LOG_IN') && (operation !== "NEW_PASSWORD")) && (
          <Item>
            {/* back to login with a logo back */ }
              <Button type="link" onClick={ () => setOperation('LOG_IN') } data-testid="login-back-link">
                <Icon type="arrow-left" /> {I18n.t("auth_messages.back")}
              </Button>
          </Item>
          ) }
          { operation === 'LOG_IN' && (
            <Item style={ { textAlign: 'center', margin: 0 } }>
            <Button type="link" onClick={ handleForgotPassword } disabled={ isLoading } data-testid="login-forgot-password-link" style={{ margin: 0 }}>
                {I18n.t("auth_messages.forgot_password")}
              </Button>
            </Item>
          ) }
          { operation === 'LOG_IN' && (
            <>
              <Item style={{ marginBottom: 0, marginTop: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', margin: '6px 0' }}>
                  <div style={{ flex: 1, height: 1, background: '#e8e8e8' }} />
                  <span style={{ padding: '0 12px', color: 'rgba(0,0,0,0.45)', fontSize: 13 }}>OR</span>
                  <div style={{ flex: 1, height: 1, background: '#e8e8e8' }} />
                </div>
              </Item>
              <Item style={{ marginTop: 0, marginBottom: 0 }}>
                <Button
                  type="default"
                  onClick={ (e) => { e.preventDefault(); e.stopPropagation(); handleOktaLogin(); } }
                  disabled={ isLoading }
                  data-testid="login-sso-button"
                  className="login-sso-btn"
                >
                  <>
                    <img src={oktaLogo} alt="" style={{ width: 22, height: 22, marginRight: 10, flexShrink: 0 }} />
                    <span>{ I18n.t('auth_messages.sign_in_with_okta') }</span>
                  </>
                </Button>
              </Item>
            </>
          ) }
        </Form>
      </div>
    </div>
  );
};

const WrappedAuthForm = Form.create({ name: 'auth_form' })(AuthForm);

export default withRouter(WrappedAuthForm);

